import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { FeatureFlag } from '@app/enums';
import { TimesheetEntry } from '@app/models/time-tracking/timesheet-entry.model';
import { Action } from '@app/modules/table/interfaces/action.interface';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import {
    TimeTrackingMenuService,
    TimesheetEntryService,
    TimesheetsIndexTable,
} from '@app/modules/time-tracking/services';
import { TimeTrackingSettingsService } from '@app/modules/time-tracking/services/time-tracking-settings.service';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import * as Sentry from '@sentry/browser';
import { PayrollIncomeType } from '@time-tracking/interfaces/settings.interface';
import { PayrollValidationHttpErrorCodes } from '@time-tracking/meta/time-tracking.meta';
import { debounceTime } from 'rxjs/operators';

/**
 * TimesheetsIndexView show a list of timesheets that
 * A) the current user can approve, or
 * B) the current user could approve if they were pending.
 *
 * It also allows them to approve all pending requests.
 */
@Component({
    templateUrl: './timesheets-index.template.html',
})
export class TimesheetsIndexView {
    @ViewChild('confirmDeleteModal') confirmDeleteModal!: ConfirmationDialogComponent;
    @ViewChild('confirmApproveAll') confirmApproveAll!: ConfirmationDialogComponent;
    @ViewChild('payrollLockedModal') payrollLockedModal!: ConfirmationDialogComponent;
    isLoading = true;
    pendingTimesheetEntryCount = 0;
    table: TimesheetsIndexTable | undefined;
    payrollIsLockedStatusCode = 423;
    hasPayrollIntegration = false;
    hasTimeClock = false;

    constructor(
        public menuService: TimeTrackingMenuService,
        private route: ActivatedRoute,
        private router: Router,
        private notifyService: NotifyService,
        private timesheetEntryService: TimesheetEntryService,
        private timeTrackingSettingsService: TimeTrackingSettingsService,
        private auth: AuthService,
        private featureService: FeatureService
    ) {}

    async ngOnInit(): Promise<void> {
        let payrollIncomeTypes: PayrollIncomeType[] = [];
        const settings = await this.timeTrackingSettingsService.get();
        this.hasPayrollIntegration = settings.payrollIntegration ?? false;
        payrollIncomeTypes = settings.payrollIncomeTypes ?? [];
        this.hasTimeClock = await this.featureService.has(FeatureFlag.timeClock);
        this.table = new TimesheetsIndexTable(
            this.auth,
            this.hasPayrollIntegration,
            this.hasTimeClock,
            payrollIncomeTypes
        );
        this.table.onLoading.pipe(debounceTime(100)).subscribe(async () => {
            try {
                await this.setPendingTimesheetEntryCountForFilters();
            } catch (e) {
                this.notifyService.error(
                    PayrollValidationHttpErrorCodes.includes(e.httpStatus ?? e.status)
                        ? e
                        : 'time-tracking.approveError'
                );
            }
        });
        this.reload();
        this.isLoading = false;
    }

    menuAction(event: MenuColumnEvent<TimesheetEntry>): void {
        const { menuItem, data } = event;
        if (!data) {
            return;
        }

        switch (menuItem.event) {
            case 'edit':
                this.onEdit(data);
                break;
            case 'delete':
                this.delete(data);
                break;
        }
    }

    onAction(action: Action<TimesheetEntry>): void {
        switch (action.event) {
            case 'approve':
                this.approve(action.data);
                break;
        }
    }

    onEdit(timesheetEntry: TimesheetEntry): void {
        this.router.navigate([timesheetEntry.id, 'edit'], { relativeTo: this.route });
    }

    async onApproveAll(): Promise<void> {
        const shouldApproveAll = await this.confirmApproveAll.open();
        if (!shouldApproveAll) {
            return;
        }

        this.isLoading = true;

        const searchPayload = this.table.paginator.currentQuery.searchPayload();
        try {
            await this.timesheetEntryService.approveAll(searchPayload);
            this.notifyService.success('time-tracking.timesheetsIndexPage.hoursApproved');
            this.isLoading = false;
            this.reload();
        } catch (e) {
            this.notifyService.error(
                PayrollValidationHttpErrorCodes.includes(e.httpStatus ?? e.status) ? e : 'time-tracking.approveError'
            );
            this.isLoading = false;
        }
    }

    async approve(timesheetEntry: TimesheetEntry): Promise<void> {
        this.isLoading = true;

        try {
            await this.timesheetEntryService.approve(timesheetEntry);
            this.notifyService.success('time-tracking.timesheetsIndexPage.hoursApproved');
            this.table?.reload();
        } catch (e) {
            Sentry.captureException(e);

            // We want to show a modal that the payroll is locked and therefore the user cannot approve this.
            if (this.payrollIsLockedStatusCode === (e.httpStatus ?? e.status) && this.hasPayrollIntegration) {
                await this.payrollLockedModal.open();
                this.isLoading = false;
                return;
            }

            this.notifyService.error(
                PayrollValidationHttpErrorCodes.includes(e.httpStatus ?? e.status) ? e : 'time-tracking.approveError'
            );
        }

        this.setPendingTimesheetEntryCount();
        this.isLoading = false;
    }

    async delete(timesheetEntry: TimesheetEntry): Promise<void> {
        const shouldDelete = await this.confirmDeleteModal.open();
        if (!shouldDelete) {
            return;
        }

        try {
            await timesheetEntry.delete();
            this.table?.reload();
        } catch (e) {
            this.notifyService.error(
                PayrollValidationHttpErrorCodes.includes(e.httpStatus ?? e.status)
                    ? e
                    : 'time-tracking.deleteErrorTimesheetEntry'
            );
        }
    }

    async reload(): Promise<void> {
        this.table?.reload();
        this.setPendingTimesheetEntryCount();
    }

    private async setPendingTimesheetEntryCount(): Promise<void> {
        const [_, meta] = await TimesheetEntry.where('supervised', true).where('status', ['pending']).limit(1).get();
        this.pendingTimesheetEntryCount = meta.pagination.total;
    }

    /*
     * We need this function in order to send the filters to BE to approve only filtered entries.
     */
    private async setPendingTimesheetEntryCountForFilters(): Promise<void> {
        const filters = this.table.paginator.currentQuery._filters;

        // Set the default filters
        let request = TimesheetEntry.where('supervised', true).where('status', ['pending']);

        // We only care about pending status so if it's only filtered by approved status, we will always set it to 0.
        if ('status' in filters && filters['status'].length === 1 && filters['status'].includes('approved')) {
            this.pendingTimesheetEntryCount = 0;
            return;
        }

        for (const [key, value] of Object.entries(filters)) {
            if (key !== 'status') {
                request = request.where(key, value);
            }
        }

        const [_, meta] = await request.limit(1).get();
        this.pendingTimesheetEntryCount = meta.pagination.total;
    }
}
